import React from "react";

/**
 * Rubik's-cube-style 3D preloader inspired by Boris Borisov (Dribbble #1964664).
 * Pure CSS, no dependencies. Color palette pulled from the original shot.
 *
 * Props:
 *   size (px) — outer side of the cube (default 96)
 *   label    — optional string shown below the cube
 *   fullScreen (bool) — if true, renders as a full-viewport splash
 */
const COLORS = {
    bg: "#252729",
    white: "#DED9D3",
    yellow: "#FEEE47",
    orange: "#F5491C",
    red: "#D2471A",
    green: "#73C287",
    deepGreen: "#6E9867",
};

/** Witty one-liners shown beneath the cube. Picked at random on every load. */
const LOADING_LINES = [
    "Solving today's schedule, one twist at a time…",
    "Aligning brilliance with bandwidth…",
    "Even Rubik took 26 years. Your dashboard? A few seconds.",
    "Untangling priorities so you don't have to…",
    "Lining up your KNOs, PASes and possibilities…",
    "Polishing your week to a mirror finish…",
    "Twisting yesterday into a better today…",
    "Sharpening your reading list…",
    "A second of patience saves a Wednesday of catching up.",
    "Conducting the orchestra of your week…",
    "Cards, calendars, conversations — handing them over.",
    "Faster than scrambling a cube. Slower than solving one.",
];
const pickLine = () => LOADING_LINES[Math.floor(Math.random() * LOADING_LINES.length)];
const Sticker = ({ color }) => (
    <svg viewBox="0 0 60 60" width="100%" height="100%" preserveAspectRatio="xMidYMid meet" className="block">
        <rect x="2" y="2" width="56" height="56" rx="6" fill={color} stroke="rgba(0,0,0,0.18)" strokeWidth="1" />
    </svg>
);

const Face = ({ color }) => (
    <div className="absolute inset-0 grid grid-cols-3 grid-rows-3 gap-[6%] p-[6%] rounded-[10%]" style={{ background: COLORS.bg }}>
        {Array.from({ length: 9 }).map((_, i) => (
            <Sticker key={i} color={color} />
        ))}
    </div>
);

export const RubiksLoader = ({ size = 96, label, showLine = true, fullScreen = false }) => {
    const halfPx = size / 2;
    // Pick a witty line once per mount; if `label` is provided explicitly, use that.
    const line = React.useMemo(() => label ?? (showLine ? pickLine() : null), [label, showLine]);
    const wrapClass = fullScreen
        ? "fixed inset-0 z-[2000] bg-slate-50 flex flex-col items-center justify-center px-6"
        : "flex flex-col items-center justify-center px-6";

    return (
        <div className={wrapClass} data-testid="rubiks-loader" role="status" aria-live="polite" aria-label="Loading">
            <div className="rubiks-stage" style={{ width: size, height: size, perspective: size * 6 }}>
                <div className="rubiks-cube" style={{ "--half": `${halfPx}px` }}>
                    <div className="rubiks-face rubiks-face-front"><Face color={COLORS.green} /></div>
                    <div className="rubiks-face rubiks-face-back"><Face color={COLORS.deepGreen} /></div>
                    <div className="rubiks-face rubiks-face-right"><Face color={COLORS.orange} /></div>
                    <div className="rubiks-face rubiks-face-left"><Face color={COLORS.red} /></div>
                    <div className="rubiks-face rubiks-face-top"><Face color={COLORS.white} /></div>
                    <div className="rubiks-face rubiks-face-bottom"><Face color={COLORS.yellow} /></div>
                </div>
            </div>
            {line && (
                <div
                    className="mt-16 sm:mt-20 max-w-md text-center text-sm text-slate-500 font-medium leading-relaxed animate-pulse"
                    data-testid="rubiks-loader-line"
                >
                    {line}
                </div>
            )}

            <style>{`
                .rubiks-stage {
                    position: relative;
                    transform-style: preserve-3d;
                }
                .rubiks-cube {
                    position: absolute;
                    inset: 0;
                    transform-style: preserve-3d;
                    animation: rubiks-tumble 4.8s cubic-bezier(0.65, 0, 0.35, 1) infinite;
                    will-change: transform;
                }
                .rubiks-face {
                    position: absolute;
                    inset: 0;
                    backface-visibility: hidden;
                    box-shadow: 0 14px 40px rgba(15, 23, 42, 0.18);
                }
                .rubiks-face-front  { transform: translateZ(var(--half)); }
                .rubiks-face-back   { transform: rotateY(180deg) translateZ(var(--half)); }
                .rubiks-face-right  { transform: rotateY(90deg)  translateZ(var(--half)); }
                .rubiks-face-left   { transform: rotateY(-90deg) translateZ(var(--half)); }
                .rubiks-face-top    { transform: rotateX(90deg)  translateZ(var(--half)); }
                .rubiks-face-bottom { transform: rotateX(-90deg) translateZ(var(--half)); }

                @keyframes rubiks-tumble {
                    0%   { transform: rotateX(-25deg) rotateY(0deg)   rotateZ(0deg); }
                    25%  { transform: rotateX(-25deg) rotateY(90deg)  rotateZ(0deg); }
                    50%  { transform: rotateX(-115deg) rotateY(90deg) rotateZ(0deg); }
                    75%  { transform: rotateX(-115deg) rotateY(180deg) rotateZ(0deg); }
                    100% { transform: rotateX(-205deg) rotateY(180deg) rotateZ(0deg); }
                }

                @media (prefers-reduced-motion: reduce) {
                    .rubiks-cube { animation: none; transform: rotateX(-25deg) rotateY(-25deg); }
                }
            `}</style>
        </div>
    );
};

export default RubiksLoader;
